import Swiper, {
  Navigation,
  Pagination,
  SwiperOptions,
  Grid,
  FreeMode,
  Keyboard,
  SwiperClass,
} from "swiper";
import { breakpoints } from "../helpers/breakpoints";

export const swiper = () => {
  // Get all of the swipers on the page
  const allSliders = document.querySelectorAll("[js-swiper]");

  // Loop over all of the sliders and apply Swiper on each one
  allSliders.length > 0
    ? allSliders.forEach((slider) => swiperInit(slider))
    : null;
};

// Create swiper carousel
export const swiperInit = (sliderElm: Element, index: number = 0) => {
  // Get id and swiper type from element
  // id is used as a unique identifier to that multiple carousels can be on the same page and not conflict with each other
  const swiperId = sliderElm.id;
  const swiperType = sliderElm.getAttribute("js-swiper-type");
  let swiperParams = <SwiperOptions>{};

  /* Accessibility Fix. Set tabindex to -1 if carousel item is not visible. Set to 0 if visible */
  const event = () => {
    document
      .querySelectorAll(`#${swiperId} a`)
      .forEach((item) => item.setAttribute("tabindex", "-1"));
    document
      .querySelectorAll(`#${swiperId} .swiper-slide-visible a`)
      .forEach((item) => item.setAttribute("tabindex", "0"));
    document
      .querySelectorAll(
        `.swiper-pagination-${swiperId} .swiper-pagination-bullet`
      )
      .forEach((item) => {
        item.setAttribute("aria-label", "change carousel page");
      });
    /* Pause videos on changing to new slide */
    document
      .querySelectorAll<HTMLVideoElement>(`#${swiperId} .swiper-slide video`)
      .forEach((item) => item.pause());
  };

  /* Update tabindex on init, slide change, and window resize */
  const events = {
    init: event,
    slideChange: event,
    resize: event,
  };

  /* Standard settings that are shared across all carousels */
  const swiperParamsBase = <SwiperOptions>{
    speed: 400,
    grabCursor: true,
    watchSlidesProgress: true,
    on: events,
    initialSlide: index,
  };

  /* If has pagination, always use these settings */
  const pagination = <SwiperOptions>{
    el: `.swiper-pagination-${swiperId}`,
    dynamicBullets: true,
    dynamicMainBullets: 3,
    clickable: true,
    bulletElement: "button",
  };

  /* If has navigation, always use these settings */
  const navigation = <SwiperOptions>{
    nextEl: `.swiper-button-next-${swiperId}`,
    prevEl: `.swiper-button-prev-${swiperId}`,
  };

  /* If has freemode, always use these settings */
  const freeMode = <SwiperOptions>{
    freeMode: { enabled: true, sticky: true },
  };

  // set parameters based on swiper type
  if (swiperType === "linkGrid") {
    // Get amount of slides - max of 3, less slides will stretch to fill the width
    const slideCount = sliderElm.getAttribute("js-swiper-count")!;
    const slidesPerView = +slideCount < 3 ? +slideCount : 3;

    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, FreeMode],
      pagination: pagination,
      freeMode: freeMode,
      slidesPerView: 2,
      spaceBetween: 12,
      breakpoints: {
        [breakpoints.screen1024]: {
          slidesPerView: slidesPerView,
          spaceBetween: 34,
        },
      },
    };
  } else if (swiperType === "promoCard") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation, FreeMode],
      pagination: pagination,
      navigation: navigation,
      freeMode: freeMode,
      slidesPerView: 1,
      spaceBetween: 22,
      breakpoints: {
        [breakpoints.screen1024]: {
          slidesPerView: 2,
          spaceBetween: 34,
        },
      },
    };
  } else if (swiperType === "topTitleImg") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation, FreeMode],
      pagination: pagination,
      navigation: navigation,
      freeMode: freeMode,
      slidesPerView: "auto",
      spaceBetween: 13,
      breakpoints: {
        [breakpoints.screen1024]: {
          slidesPerView: "auto",
          spaceBetween: 34,
        },
      },
    };
  } else if (swiperType === "flexibleCards") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation, FreeMode],
      pagination: pagination,
      navigation: navigation,
      freeMode: freeMode,
      slidesPerView: 1,
      spaceBetween: 13,
      breakpoints: {
        [breakpoints.screen1024]: {
          slidesPerView: 3,
          spaceBetween: 34,
        },
      },
    };
  } else if (swiperType === "featuredHotels") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation, Grid, FreeMode],
      pagination: pagination,
      navigation: navigation,
      freeMode: freeMode,
      slidesPerView: 2,
      spaceBetween: 13,
      grid: {
        rows: 2,
        fill: "row",
      },
      breakpoints: {
        [breakpoints.screen1024]: {
          slidesPerView: 4,
          spaceBetween: 34,
          grid: {
            rows: 1,
          },
        },
      },
    };
  } else if (swiperType === "featuredHotelsMyAccount") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation, FreeMode],
      pagination: pagination,
      navigation: navigation,
      freeMode: freeMode,
      slidesPerView: 2,
      spaceBetween: 13,
      breakpoints: {
        [breakpoints.screen1024]: {
          slidesPerView: 3,
          spaceBetween: 34,
        },
      },
    };
  } else if (swiperType === "galleryMob") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination],
      pagination: pagination,
      slidesPerView: 1,
    };
  } else if (swiperType === "galleryModal") {
    const fraction = document.getElementById("fraction");
    const slides = sliderElm.querySelectorAll(".swiper-slide");
    const slideCount = slides.length;

    if (!fraction) {
      return;
    }

    fraction.textContent = `${index + 1} / ${slideCount}`;

    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation, Keyboard],
      pagination: pagination,
      navigation: navigation,
      keyboard: { enabled: true },
      slidesPerView: 1,
      on: {
        slideChange: (newSwiper) => {
          fraction.textContent = `${newSwiper.activeIndex + 1} / ${
            newSwiper.slides.length
          }`;
        },
      },
    };
  } else if (swiperType === "slimPromoCard") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation, FreeMode],
      pagination: pagination,
      navigation: navigation,
      freeMode: freeMode,
      slidesPerView: "auto",
      centeredSlides: true,
      spaceBetween: 0,
      breakpoints: {
        [breakpoints.screen1024]: {
          centeredSlides: false,
        },
      },
    };
  } else if (swiperType === "guestServicesExtraBtns") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [FreeMode],
      freeMode: freeMode,
      slidesPerView: "auto",
      spaceBetween: 10,
      breakpoints: {
        [breakpoints.screen1024]: {
          spaceBetween: 15,
        },
      },
    };
  } else if (swiperType === "reviewsSwiper") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation, FreeMode],
      pagination: pagination,
      navigation: navigation,
      freeMode: freeMode,
      slidesPerView: "auto",
      spaceBetween: 25,
      breakpoints: {
        [breakpoints.screen1024]: {
          slidesPerView: 3,
          spaceBetween: 43,
        },
      },
    };
  } else if (swiperType === "fullImageCarousel") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation],
      pagination: pagination,
      navigation: navigation,
      slidesPerView: 1,
    };
  } else if (swiperType === "carouselPromo") {
    swiperParams = {
      ...swiperParamsBase,
      modules: [Pagination, Navigation],
      pagination: pagination,
      navigation: navigation,
      slidesPerView: 1,
    };
  }

  // create carousel using unique identifer and params
  return new Swiper(`#${swiperId}`, swiperParams);
};

export const swiperDestroy = (sliderElm: SwiperClass) => {
  sliderElm?.swiper?.destroy();
};
